import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
// import Radio from "@mui/material/Radio";
// import RadioGroup from "@mui/material/RadioGroup";
import SvgIcon from "@mui/material/SvgIcon";
import Tooltip from "@mui/material/Tooltip";
import { useContext, useState } from "react";
import { InfoCircle } from "react-bootstrap-icons";
import "../../../../Tooltip/styles/tooltip.css";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import { FormContext } from "../context/FormProvider";
import * as Messages from "../messages/messages";
import "../styles/form-container.css";
import "./styles/radio-group-field.css";

/**
 * RadioGroupField Component
 * @description A component that renders a mui radio made specifically for Yes/No inputs.
 * @param {Object} props - The component props.
 * @param {string} props.label - The label for the radio field.
 * @param {string} props.idProp - The id of the radio field.
 * @param {Object} props.state - The state object.
 * @param {Function} props.setState - The state setter function.
 * @param {Object} props.slide2 - The state object for slide 2.
 * @param {Function} props.setSlide2 - The state setter function for slide 2.
 * @param {Object} props.slide7 - The state object for slide 7.
 * @param {Function} props.setSlide7 - The state setter function for slide 7.
 * @param {Object} props.debouncedSave - The debounced save function.
 * @param {Function} props.setIsLoading - The loading state setter function.
 * @param {boolean} props.disabledInput - A boolean indicating if the input is disabled.
 * @returns {JSX.Element} The rendered RadioGroupField component.
 */
const RadioGroupField = ({
  label,
  idProp,
  state,
  setState,
  debouncedSave,
  setIsLoading,
  disabledInput,
}) => {
  const [open, setOpen] = useState(false);
  const { slide1, slide6, handleInputDisable } = useContext(FormContext);

  const { userId } = useContext(DashboardContext);

  const resetFields = {
    onNativeLand: { submittingToRegAgency: "No", regAgencySubmitDate: "" },
    submittingToRegAgency: { regAgencySubmitDate: "" },
    activeCDX: { cdxAccountManager: "", cdxUserId: "" },
    inspectionRequest: {
      inspectingCompany: "",
      inspectorName: "",
      inspectorPhone: "",
      inspectorEmail: "",
      inspectionRecipient1: "",
      inspectionRecipient1Title: "",
      inspectionRecipient1Email: "",
      inspectionRecipient2: "",
      inspectionRecipient2Title: "",
      inspectionRecipient2Email: "",
      inspectionRecipient3: "",
      inspectionRecipient3Title: "",
      inspectionRecipient3Email: "",
      inspectionRecipient4: "",
      inspectionRecipient4Title: "",
      inspectionRecipient4Email: "",
    },
    signRequest: { sign3x3: false, signStand: false },
    dustControlRequest: {
      manualLogsRequest: "No",
      dcc: "",
      dccPhone: "",
      dccEmail: "",
      dccTitle: "",
      dustAuditRequest: "No",
      dustAuditContact: "",
      dustAuditContactPhone: "",
      dustAuditContactEmail: "",
      dustSignRequest: "No",
      dcName: "",
      dcPhone: "",
    },
    manualLogsRequest: { dcc: "", dccPhone: "", dccEmail: "", dccTitle: "" },
    dustAuditRequest: {
      dustAuditContact: "",
      dustAuditContactPhone: "",
      dustAuditContactEmail: "",
    },
    dustSignRequest: { dcName: "", dcPhone: "" },
    isLinear: { eLat: "", eLng: "" },
    layDownYard: { layDownYardDist: "" },
    portableTanksOnsite: { portableTankCapacity: "" },
    delegatedRepCheck: {
      odrCompany: "",
      odrName: "",
      odrPhone: "",
      odrEmail: "",
      odrTitle: "",
    },
    safetyInterest: {
      level1Safety: "",
      level2Safety: "",
    },
  };

  // Function to handle the radio group change
  const handleCheckboxChange = (e) => {
    let newState = { ...state };
    const realID = e.target.id.split("-")[0];
    const clickedValue = e.target.value;
    const currentValue = state[realID];

    let updatedValue;

    // If clicking the same value again, deselect (set to "")
    if (clickedValue === currentValue) {
      updatedValue = "";
    } else {
      updatedValue = clickedValue;
    }

    newState[realID] = updatedValue;

    // If updated value is "No", clear related fields
    if (updatedValue === "No") {
      if (realID === "ownerGCCheck") {
        newState = {
          ...newState,
          ownerGCCheck: "No",
          gcName: "",
          gcAdd: "",
          gcCity: "",
          gcState: "",
          gcZip: "",
          gcPhone: "",
          gcContactName: "",
          gcContactEmail: "",
          gcContactPhone: "",
          gcContactTitle: "",
        };
        setState(newState);
      } else if (resetFields[realID]) {
        newState = { ...newState, ...resetFields[realID] };
      }
    }

    // If updated value is "Yes" and ownerGCCheck, populate fields
    if (updatedValue === "Yes" && realID === "ownerGCCheck") {
      console.log("ownerGCCheck is Yes, populating fields");
      newState = {
        ...newState,
        ownerGCCheck: "Yes",
        gcName: slide6.oName,
        gcAdd: slide6.oAdd,
        gcState: slide6.oState,
        gcCity: slide6.oCity,
        gcZip: slide6.oZip,
        gcPhone: slide6.oPhone,
        gcContactName: slide6.oContactName,
        gcContactEmail: slide6.oContactEmail,
        gcContactPhone: slide6.oContactPhone,
        gcContactTitle: slide6.oContactTitle,
      };
      console.log("newState after ownerGCCheck Yes:", newState);
      setState(newState);
    }

    if (
      updatedValue === "Yes" &&
      (realID === "manualLogsRequest" ||
        realID === "dustAuditRequest" ||
        realID === "dustSignRequest")
    ) {
      setState({
        ...newState,
        dustControlRequest: "Yes",
      });
    } else if (updatedValue === "Yes" && realID === "level1Safety") {
      newState = {
        ...newState,
        safetyInterest: "Yes",
        level2Safety: "No",
      };
      setState(newState);
    } else if (updatedValue === "Yes" && realID === "level2Safety") {
      newState = {
        ...newState,
        safetyInterest: "Yes",
        level1Safety: "No",
      };
      setState(newState);
    } else {
      setState(newState);
    }

    if (debouncedSave && setIsLoading) {
      setIsLoading(true);
      debouncedSave.mutate(newState);
    }
  };

  return (
    <>
      <div
        className="input-radio-group-container"
        id={`${idProp}-radio-container`}
      >
        <label
          id={`${idProp}-label`}
          // className="radio-group-form-label"
          className="text-form-label"
          htmlFor={`radio-${idProp}`}
        >
          {label}:{" "}
        </label>
        <div
          className="input-info-radio-group-container"
          id={`${idProp}-info-container`}
        >
          <FormGroup
            row={true}
            className="radio-grouping"
            aria-label={`${label} radio group`}
            name={`radio-${idProp}`}
            id={`radio-${idProp}`}
            value={state[idProp]}
            onChange={handleCheckboxChange}
            classes={{
              row: "radio-group-row",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  id={`${idProp}-true`}
                  value={"Yes"}
                  checked={state[idProp] === "Yes"}
                  disabled={
                    handleInputDisable(idProp, state, disabledInput, userId) ||
                    (idProp === "submittingToRegAgency" &&
                      slide1.onNativeLand == "Yes")
                  }
                  classes={{
                    root: "radio-group-btn-true",
                    checked: "radio-group-btn-checked",
                  }}
                  icon={
                    <SvgIcon
                      component={CheckBoxOutlineBlankIcon}
                      className="radio-group-btn-icon-true"
                      viewBox="3.6 3.6 16.8 16.8"
                    />
                  }
                  checkedIcon={
                    <SvgIcon
                      component={CheckBoxIcon}
                      className="radio-group-btn-icon-true"
                      viewBox="3.6 3.6 16.8 16.8"
                    />
                  }
                />
              }
              label="Yes"
              id={`true-label-group`}
              classes={{
                label: "radio-group-btn-label",
                root: "radio-group-form-control-label",
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  id={`${idProp}-false`}
                  value={"No"}
                  checked={state[idProp] === "No"}
                  disabled={
                    handleInputDisable(idProp, state, disabledInput, userId) ||
                    (idProp === "submittingToRegAgency" &&
                      slide1.onNativeLand == "Yes")
                  }
                  classes={{
                    root: "radio-group-btn-false",
                    checked: "radio-group-btn-checked",
                  }}
                  icon={
                    <SvgIcon
                      component={CheckBoxOutlineBlankIcon}
                      className="radio-group-btn-icon-false"
                      viewBox="3 3 18 18"
                    />
                  }
                  checkedIcon={
                    <SvgIcon
                      component={CloseIcon}
                      className="radio-group-btn-icon-false"
                      viewBox="2 2 20 20"
                    />
                  }
                />
              }
              label="No"
              id={`false-label-group`}
              classes={{
                label: "radio-group-btn-label",
                root: "radio-group-form-control-label",
              }}
            />
            {(idProp === "activeMyDeq" || idProp === "activeCDX") && (
              <FormControlLabel
                control={
                  <Checkbox
                    id={`${idProp}-unknown`}
                    value={"Unknown"}
                    checked={state[idProp] === "Unknown"}
                    disabled={handleInputDisable(
                      idProp,
                      state,
                      disabledInput,
                      userId,
                    )}
                    classes={{
                      root: "radio-group-btn-unknown",
                      checked: "radio-group-btn-checked",
                    }}
                    icon={
                      <SvgIcon
                        component={CheckBoxOutlineBlankIcon}
                        className="radio-group-btn-icon-unknown"
                        viewBox="3.6 3.6 16.8 16.8"
                      />
                    }
                    checkedIcon={
                      <SvgIcon
                        component={CheckBoxIcon}
                        className="radio-group-btn-icon-unknown"
                        viewBox="3.6 3.6 16.8 16.8"
                      />
                    }
                  />
                }
                label="Unknown"
                id={`unknown-label-group`}
                classes={{
                  label: "radio-group-btn-label",
                  root: "radio-group-form-control-label",
                }}
              />
            )}
            {idProp === "onNativeLand" && (
              <FormControlLabel
                control={
                  <Checkbox
                    id={`${idProp}-both`}
                    value={"Both"}
                    checked={state[idProp] === "Both"}
                    disabled={handleInputDisable(
                      idProp,
                      state,
                      disabledInput,
                      userId,
                    )}
                    classes={{
                      root: "radio-group-btn-both",
                      checked: "radio-group-btn-checked",
                    }}
                    icon={
                      <SvgIcon
                        component={CheckBoxOutlineBlankIcon}
                        className="radio-group-btn-icon-both"
                        viewBox="3.6 3.6 16.8 16.8"
                      />
                    }
                    checkedIcon={
                      <SvgIcon
                        component={CheckBoxIcon}
                        className="radio-group-btn-icon-both"
                        viewBox="3.6 3.6 16.8 16.8"
                      />
                    }
                  />
                }
                label="Both"
                id={`both-label-group`}
                classes={{
                  label: "radio-group-btn-label",
                  root: "radio-group-form-control-label",
                }}
              />
            )}
          </FormGroup>
          <Tooltip
            id="rg-tooltip"
            classes={{
              tooltip: "radio-group-tooltip",
              popper: "radio-group-popper",
              arrow: "radio-group-arrow",
              tooltipPlacementRight: "radio-group-right",
            }}
            title={Messages[`${idProp}Message`] || ""}
            open={open}
            arrow
            placement="right"
          >
            <InfoCircle
              className="radio-group-info-circ"
              id={`info-${idProp}`}
              color="white"
              size={30}
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={() => setOpen(false)}
            />
          </Tooltip>
        </div>
      </div>
    </>
  );
};

export default RadioGroupField;
